import { queryOptions, useMutation } from '@tanstack/react-query'
import { api, unwrap } from '../client'
import { queryKeys, type ListParams } from '../keys'
import { useInfiniteList } from '../pagination'
import { fetchAll } from '../fetch-all'
import { MAX_PAGE_SIZE, type CreateApplication } from '../types'

export function fetchApplicationsPage(params: ListParams, cursor?: string) {
  return unwrap(
    api.GET('/admin/v1/applications', {
      params: {
        query: {
          limit: Math.min(params.limit ?? 50, MAX_PAGE_SIZE),
          cursor,
          q: params.q || undefined,
        },
      },
    }),
  )
}

/** Paged list for tables. */
export function useApplicationsList(params: ListParams = {}) {
  return useInfiniteList({
    queryKey: queryKeys.applications.list(params),
    fetchPage: (cursor) => fetchApplicationsPage(params, cursor),
  })
}

/**
 * Every application, for pickers (scope selector, command palette). There are
 * tens of applications, not thousands, so one large page is fine.
 */
export const allApplicationsQuery = queryOptions({
  queryKey: queryKeys.applications.list({ all: true }),
  queryFn: () => fetchAll((cursor) => fetchApplicationsPage({ limit: MAX_PAGE_SIZE }, cursor)),
  staleTime: 60_000,
})

export const applicationQuery = (id: string) =>
  queryOptions({
    queryKey: queryKeys.applications.detail(id),
    queryFn: () => unwrap(api.GET('/admin/v1/applications/{id}', { params: { path: { id } } })),
  })

/** Errors are left to the caller (the create form maps them onto fields). */
export function useCreateApplication() {
  return useMutation({
    mutationFn: (body: CreateApplication) => unwrap(api.POST('/admin/v1/applications', { body })),
    meta: {
      invalidates: [queryKeys.applications.all],
      successMessage: 'Application created',
      errorToast: false,
    },
  })
}
